import { alert } from 'actions/flash'
import {
  useGetCheckoutSettingsQuery,
  useUpdateCheckoutSettingsMutation,
} from 'api/checkoutSettings'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import { useCallback, useEffect, useState } from 'react'
import { useAbility } from 'utilities/ability'
import { errorHandler } from 'utilities/errorHandler'

const t = buildTranslate('cms.checkout_message')

export type Mode = 'empty' | 'editing' | 'viewing'

export default function useCheckoutMessage() {
  const ability = useAbility()
  const canEdit = ability.can('manage', 'OrderingManagement')
  const dispatch = useDispatch()
  const { data, isLoading, isError } = useGetCheckoutSettingsQuery()
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateCheckoutSettingsMutation()
  const [mode, setMode] = useState<Mode>('empty')
  const [editMessage, setEditMessage] = useState('')
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)

  const existingMessage = data?.ordering_config?.checkout_message || ''

  const displayAlert = useCallback(
    (key: 'danger' | 'success', messageKey: string) => {
      dispatch(
        alert({
          key,
          message: t(`alerts.${messageKey}`),
          timeout: 5,
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    if (isError) {
      displayAlert('danger', 'fetch_error')
    }
  }, [isError, displayAlert])

  useEffect(() => {
    if (data && !isLoading && mode !== 'editing') {
      if (existingMessage) {
        setMode('viewing')
      } else {
        setMode('empty')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, existingMessage])

  const handleAdd = useCallback(() => {
    setEditMessage('')
    setMode('editing')
  }, [])

  const handleEdit = useCallback(() => {
    setEditMessage(existingMessage)
    setMode('editing')
  }, [existingMessage])

  const handleExitWithoutSaving = useCallback(() => {
    if (existingMessage) {
      setMode('viewing')
    } else {
      setMode('empty')
    }
  }, [existingMessage])

  const handleSave = useCallback(async () => {
    const isEmpty = editMessage.trim() === ''
    try {
      await updateSettings({
        checkout_message: isEmpty ? null : editMessage,
      }).unwrap()
      displayAlert('success', isEmpty ? 'remove_success' : 'save_success')
      setMode(isEmpty ? 'empty' : 'viewing')
    } catch (error) {
      errorHandler.capture('Failed to save checkout message', { error })
      displayAlert('danger', isEmpty ? 'remove_error' : 'save_error')
    }
  }, [editMessage, updateSettings, displayAlert])

  const handleRemove = useCallback(async () => {
    try {
      await updateSettings({
        checkout_message: null,
      }).unwrap()
      displayAlert('success', 'remove_success')
      setMode('empty')
    } catch (error) {
      errorHandler.capture('Failed to remove checkout message', { error })
      displayAlert('danger', 'remove_error')
    }
  }, [updateSettings, displayAlert])

  return {
    mode,
    editMessage,
    existingMessage,
    isLoading,
    isSaving,
    canEdit,
    isRemoveModalOpen,
    handleAdd,
    handleEdit,
    handleExitWithoutSaving,
    handleSave,
    handleRemove,
    setEditMessage,
    setIsRemoveModalOpen,
  }
}
